<script>
import TranscriptSummaryCard from '@modules/crm/components/transcript-summary-card/transcript-summary-card.vue'
import TranscriptSummaryItem from '@modules/crm/components/transcript-summary-item/transcript-summary-item.vue'
import TranscriptStateFeedback from '@modules/crm/components/transcript-state-feedback/transcript-state-feedback.vue'

export default {
  name: 'TranscriptSummaryPanel',
  components: {
    TranscriptSummaryCard,
    TranscriptSummaryItem,
    TranscriptStateFeedback,
  },
  props: {
    summary: { type: Object, default: null },
    aiStatus: { type: String, default: '' },
    transcriptStatus: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    canRegenerate: { type: Boolean, default: false },
    // Inverte cinza/branco dos cards e itens — usado quando o painel vive sobre
    // um fundo já cinza (timeline do Omnichannel) sem regressão no modal.
    inverted: { type: Boolean, default: false },
  },
  computed: {
    isCompleted() {
      return this.aiStatus === 'completed' && Boolean(this.summary)
    },
    isProcessing() {
      return this.aiStatus === 'queued' || this.aiStatus === 'processing'
    },
    isFailed() {
      return this.aiStatus === 'failed'
    },
    isPendingNotStarted() {
      return (
        (this.aiStatus === 'pending' || this.aiStatus === 'skipped') &&
        this.transcriptStatus !== 'ended'
      )
    },
    isPendingEnded() {
      return (
        (this.aiStatus === 'pending' || this.aiStatus === 'skipped') &&
        this.transcriptStatus === 'ended'
      )
    },
    resumoText() {
      // O backend (aawz_transcript_summaries) entrega o texto em `summary.summary`.
      // Mantém fallback para `resumo` (formato legado/alternativo).
      if (!this.summary) return ''
      return this.summary.summary || this.summary.resumo || ''
    },
    decisions() {
      return (this.summary && this.summary.decisions) || []
    },
    nextSteps() {
      return (this.summary && this.summary.next_steps) || []
    },
    opportunities() {
      return (this.summary && this.summary.opportunities) || []
    },
    aiErrorMessage() {
      return (this.summary && this.summary.ai_error_message) || 'Erro ao gerar o resumo. Tente novamente.'
    },
    processingLabel() {
      if (this.aiStatus === 'queued') return 'Aguardando na fila...'
      return 'Gerando resumo com IA...'
    },
    // Props do TranscriptStateFeedback p/ os estados não-concluídos (ordem de
    // prioridade: processando → erro → não gerado → aguardando fim → loading).
    feedback() {
      if (this.isProcessing) {
        return { state: 'processing', message: this.processingLabel }
      }
      if (this.isFailed) {
        return { state: 'error', message: this.aiErrorMessage }
      }
      if (this.isPendingEnded) {
        return {
          state: 'empty',
          icon: 'mdi-text-box-outline',
          message: 'Resumo ainda não gerado',
        }
      }
      if (this.isPendingNotStarted) {
        return {
          state: 'processing',
          icon: 'mdi-clock-outline',
          message: 'O resumo será gerado automaticamente ao final da reunião',
        }
      }
      if (this.loading) return { state: 'loading' }
      return { state: 'empty', message: 'Sem informações de resumo disponíveis.' }
    },
  },
  methods: {
    // O backend entrega decisões/próximos passos/oportunidades como objetos
    // ({ data, texto } / { texto, responsavel }). Extraímos só o texto — nunca
    // exibir o objeto cru. Aceita string (formato legado) e variações de chave.
    itemText(item) {
      if (typeof item === 'string') return item
      if (!item) return ''
      return (
        item.texto ||
        item.text ||
        item.descricao ||
        item.description ||
        item.label ||
        item.title ||
        ''
      )
    },
    itemResponsavel(item) {
      if (!item || typeof item === 'string') return ''
      return item.responsavel || item.responsible || ''
    },
    onRegenerate() {
      this.$emit('regenerate')
    },
  },
}
</script>

<template>
  <div class="transcript-summary-panel">
    <!-- State 1: completed with summary -->
    <div v-if="isCompleted">
      <TranscriptSummaryCard v-if="resumoText" title="Resumo" :inverted="inverted">
        <p class="az-text-primary az-size-14 mb-0">{{ resumoText }}</p>
      </TranscriptSummaryCard>

      <TranscriptSummaryCard
        v-if="decisions.length"
        title="Decisões"
        :inverted="inverted"
      >
        <div class="d-flex flex-column az-gap-small">
          <TranscriptSummaryItem
            v-for="(decision, i) in decisions"
            :key="i"
            icon="mdi-gavel"
            :text="itemText(decision)"
            :inverted="inverted"
          />
        </div>
      </TranscriptSummaryCard>

      <TranscriptSummaryCard
        v-if="nextSteps.length"
        title="Próximos passos"
        :inverted="inverted"
      >
        <div class="d-flex flex-column az-gap-small">
          <TranscriptSummaryItem
            v-for="(step, i) in nextSteps"
            :key="i"
            icon="check-circle"
            icon-color="success"
            :text="itemText(step)"
            :inverted="inverted"
          >
            <template v-if="itemResponsavel(step)" v-slot:right>
              <v-chip x-small color="#E8EAF1" text-color="#5B5B5B">
                {{ itemResponsavel(step) }}
              </v-chip>
            </template>
          </TranscriptSummaryItem>
        </div>
      </TranscriptSummaryCard>

      <TranscriptSummaryCard
        v-if="opportunities.length"
        title="Oportunidades identificadas"
        :inverted="inverted"
      >
        <div class="d-flex flex-column az-gap-small">
          <TranscriptSummaryItem
            v-for="(opp, i) in opportunities"
            :key="i"
            icon="lamp-on"
            icon-color="warning-new"
            :text="itemText(opp)"
            :inverted="inverted"
          />
        </div>
      </TranscriptSummaryCard>
    </div>

    <!-- Demais estados (aguardando IA / erro / não gerado / loading) — todos no
         TranscriptStateFeedback (com sombra). A ação de retry do erro fica no
         banner do topo; aqui só o "Gerar resumo" do estado pendente-finalizado. -->
    <TranscriptStateFeedback v-else v-bind="feedback">
      <template v-if="isPendingEnded" v-slot:button>
        <BaseButton sm fitContent primary @click="onRegenerate">
          Gerar resumo
        </BaseButton>
      </template>
    </TranscriptStateFeedback>
  </div>
</template>

<style lang="sass" scoped>
.transcript-summary-panel
  width: 100%
</style>
